/*
 * nd-lead-capture.js — module de capture de leads partagé (Neurodidacte)
 * ---------------------------------------------------------------------
 * Une seule implémentation, pilotée par des data-* sur le <form>.
 *
 * POURQUOI CE MODULE EXISTE
 * Les 5 sections de capture historiques (leadmagnet-form, email-action-form,
 * cafe-virtuel, quiz-formation, essai-pieges-form) dupliquent le même pipeline
 * et partagent deux défauts :
 *   1. chaque destination se termine par `.catch(function(){})`, donc
 *      `Promise.all(...).then(redirect)` redirige TOUJOURS — même quand tout a
 *      échoué. L'internaute voit « C'est confirmé » alors que le lead est perdu.
 *   2. la soumission Customer.io passe par un <form> caché ciblant une <iframe>.
 *      L'événement `load` de l'iframe se déclenche même quand le domaine est
 *      bloqué (page d'erreur) → un blocage est INDÉTECTABLE.
 *
 * CE QUE CE MODULE FAIT DIFFÉREMMENT
 * - Une SONDE précède la soumission : un GET `no-cors` vers le domaine
 *   Customer.io. Si le domaine est bloqué (bloqueur de publicité) ou injoignable,
 *   `fetch` rejette. C'est le signal d'échec qui manquait, et il est obtenu SANS
 *   toucher au mécanisme de soumission.
 * - La soumission elle-même reste le POST de <form> caché vers <iframe> cachée,
 *   c'est-à-dire exactement ce qui fonctionne en production aujourd'hui. On
 *   n'invente pas un nouveau mécanisme de capture le jour d'un lancement.
 * - Si la sonde échoue → on n'envoie RIEN et on n'affiche AUCUN succès : message
 *   d'erreur, bouton réactivé, valeurs conservées. On ne ment jamais, et aucune
 *   soumission en double n'est possible.
 *
 * CE QU'IL NE FAIT PAS, ET POURQUOI
 * - Pas de `form_type=customer` : pour un courriel déjà client, Shopify ne
 *   renvoie ni erreur ni succès (anti-énumération) → perte invisible des leads
 *   les plus chauds. De plus hCaptcha protège officiellement ce type de
 *   formulaire, et hCaptcha est un tiers (hcaptcha.com).
 * - Pas de tracker `_cio` : le thème charge le CDP (`window.cioanalytics`), pas
 *   l'API legacy. `window._cio` est `undefined` ici.
 * - Le POST `form_type=contact` est conservé en « meilleur effort » seulement
 *   (il n'envoie qu'un courriel au marchand et peut être filtré par hCaptcha) :
 *   il ne compte JAMAIS comme une preuve de capture.
 *
 * CONTRAT
 * Le <form> visible porte des champs nommés `contact[...]`. Le module les
 * récolte génériquement — aucun champ à déclarer en double côté Liquid.
 * Configuration via data-* (voir readConfig()).
 */
(function () {
  'use strict';

  var CIO_ENDPOINT = 'https://eu.customerioforms.com/forms/submit_action';
  var HS_ENDPOINT = 'https://api.hsforms.com/submissions/v3/integration/submit/';
  var CIO_TIMEOUT_MS = 8000;

  function readConfig(form) {
    var d = form.dataset;
    return {
      cioSiteId: (d.cioSiteId || '').trim(),
      cioFormId: (d.cioFormId || '').trim(),
      hsPortalId: (d.hsPortalId || '').trim(),
      hsFormGuid: (d.hsFormGuid || '').trim(),
      webhookUrl: (d.webhookUrl || '').trim(),
      redirectUrl: (d.redirect || '').trim(),
      formName: (d.formName || 'lead').trim(),
      pageHandle: (d.pageHandle || '').trim(),
      gadsLabel: (d.gadsLabel || '').trim(),        // étiquette Google Ads, vide = rien n'est envoyé
      // Ce module sert plusieurs entonnoirs (webinaire, guide, …) : le message de
      // consentement ne peut donc pas nommer le webinaire en dur.
      consentError:
        (d.consentError || '').trim() ||
        'Veuillez accepter de recevoir les courriels pour continuer.',
      shopifyNotify: d.shopifyNotify === 'true'
    };
  }

  function isValidEmail(v) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v);
  }

  /* Récolte tous les champs `contact[...]` du formulaire visible.
     Les radios/cases non cochées sont ignorées par FormData, ce qui est le
     comportement voulu. */
  function harvest(form) {
    var out = {};
    var fd = new FormData(form);
    fd.forEach(function (value, key) {
      var m = /^contact\[(.+)\]$/.exec(key);
      if (!m) return;
      var v = typeof value === 'string' ? value.trim() : value;
      if (v !== '') out[m[1]] = v;
    });
    return out;
  }

  function toUrlEncoded(pairs) {
    var body = new URLSearchParams();
    Object.keys(pairs).forEach(function (k) {
      body.set(k, pairs[k]);
    });
    return body;
  }

  /* --- Destination 1 : Customer.io Forms (PRIMAIRE, seule preuve de capture) ---
   *
   * Deux étapes, et c'est délibéré.
   *
   * 1) SONDE (`probeCustomerIo`) — un GET `no-cors` vers le domaine Customer.io.
   *    Une réponse opaque suffit : on ne veut pas la lire, seulement savoir si le
   *    domaine est joignable. Un bloqueur de publicité, un DNS menteur ou une
   *    coupure réseau font REJETER `fetch`. C'est notre seul signal d'échec fiable.
   *
   *    Pourquoi une sonde séparée ? Parce qu'on ne peut PAS détecter l'échec sur la
   *    soumission elle-même : l'<iframe> cachée déclenche `load` même quand le
   *    domaine est bloqué, et la page d'arrivée est de toute façon illisible
   *    (`X-Frame-Options: DENY`, `frame-ancestors 'none'` sur la boutique).
   *
   * 2) SOUMISSION (`postCustomerIoForm`) — un vrai POST de <form> caché vers une
   *    <iframe> cachée. C'est EXACTEMENT le mécanisme qui tourne en production
   *    aujourd'hui sur les autres pages de capture. On ne le remplace pas par un
   *    `fetch` : le jour d'un lancement, on ne met pas en service un mécanisme de
   *    capture non éprouvé.
   *
   * Si la sonde échoue, on n'envoie RIEN et on n'affiche AUCUN succès. Aucune
   * soumission en double n'est donc possible.
   */

  function probeCustomerIo() {
    return fetch(CIO_ENDPOINT, { method: 'GET', mode: 'no-cors', cache: 'no-store' });
  }

  function postCustomerIoForm(cfg, data) {
    // ⚠️ `success_url` est OBLIGATOIRE : sans lui l'endpoint répond 400 et rien
    // n'est enregistré (vérifié le 2026-07-13 : sans → 400 ; avec → 302 et la
    // personne apparaît bien dans Customer.io, attributs mappés).
    // La redirection serveur n'est jamais suivie (on redirige nous-mêmes en JS) ;
    // l'URL doit simplement être valide.
    var successUrl = window.location.origin + (cfg.redirectUrl || '/');

    var action =
      CIO_ENDPOINT +
      '?site_id=' + encodeURIComponent(cfg.cioSiteId) +
      '&form_id=' + encodeURIComponent(cfg.cioFormId) +
      '&success_url=' + encodeURIComponent(successUrl);

    return new Promise(function (resolve) {
      var frameName = 'nd-cio-frame-' + Date.now();

      var iframe = document.createElement('iframe');
      iframe.name = frameName;
      iframe.setAttribute('tabindex', '-1');
      iframe.setAttribute('aria-hidden', 'true');
      iframe.style.cssText = 'display:none!important;position:absolute;left:-9999px;width:0;height:0;border:0;';

      var form = document.createElement('form');
      form.method = 'POST';
      form.action = action;
      form.target = frameName;
      form.style.cssText = 'display:none!important;';

      Object.keys(data).forEach(function (k) {
        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'contact[' + k + ']';
        input.value = data[k];
        form.appendChild(input);
      });

      document.body.appendChild(iframe);
      document.body.appendChild(form);

      var settled = false;
      function done() {
        if (settled) return;
        settled = true;
        // On laisse l'iframe/le form en place le temps que la requête parte,
        // puis on nettoie.
        setTimeout(function () {
          if (form.parentNode) form.parentNode.removeChild(form);
          if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
        }, 2000);
        resolve(true);
      }

      iframe.addEventListener('load', done);
      setTimeout(done, CIO_TIMEOUT_MS);
      form.submit();
    });
  }

  function sendToCustomerIo(cfg, data) {
    if (!cfg.cioFormId || !cfg.cioSiteId) return Promise.resolve(false);
    return probeCustomerIo().then(function () {
      return postCustomerIoForm(cfg, data);
    });
  }

  /* --- Destination 2 : HubSpot Forms API (désactivée si GUID vide) --- */
  function sendToHubSpot(cfg, data) {
    if (!cfg.hsPortalId || !cfg.hsFormGuid) return Promise.resolve(false);

    var map = { prenom: 'firstname', nom: 'lastname' };
    var fields = Object.keys(data).map(function (k) {
      return { name: map[k] || k, value: data[k] };
    });

    var payload = {
      fields: fields,
      context: { pageUri: window.location.href, pageName: document.title }
    };
    var hutk = document.cookie.match(/hubspotutk=([^;]+)/);
    if (hutk) payload.context.hutk = hutk[1];

    return fetch(HS_ENDPOINT + cfg.hsPortalId + '/' + cfg.hsFormGuid, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: { 'Content-Type': 'application/json' }
    })
      .then(function (r) {
        return r.ok;
      })
      .catch(function () {
        return false;
      });
  }

  /* --- Destination 3 : notification Shopify « meilleur effort » ---------------
     `form_type=contact` ne crée AUCUN enregistrement : il envoie un courriel à
     l'adresse d'expédition de la boutique (doc Shopify). hCaptcha peut le filtrer
     en silence. On l'envoie parce que ça peut sauver un lead, jamais comme preuve. */
  function notifyShopify(cfg, data) {
    if (!cfg.shopifyNotify) return Promise.resolve(false);

    var pairs = { form_type: 'contact', utf8: '✓' };
    Object.keys(data).forEach(function (k) {
      pairs['contact[' + k + ']'] = data[k];
    });

    return fetch(window.location.pathname, {
      method: 'POST',
      body: toUrlEncoded(pairs).toString(),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
      .then(function () {
        return true;
      })
      .catch(function () {
        return false;
      });
  }

  /* --- Destination 4 : webhook générique (Make, Zapier…) — optionnel --- */
  function sendToWebhook(cfg, data, meta) {
    if (!cfg.webhookUrl) return Promise.resolve(false);
    var payload = { form_name: cfg.formName, page_handle: cfg.pageHandle };
    Object.keys(data).forEach(function (k) {
      payload[k] = data[k];
    });
    Object.keys(meta).forEach(function (k) {
      payload[k] = meta[k];
    });
    return fetch(cfg.webhookUrl, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: { 'Content-Type': 'application/json' }
    })
      .then(function () {
        return true;
      })
      .catch(function () {
        return false;
      });
  }

  /* --- Événement client Shopify → Custom Pixel « Meta Lead » → CAPI -----------
     ⚠️ L'allowlist des page_handle qui déclenchent un `Lead` vit dans le Custom
     Pixel (Admin → Événements client), PAS ici. Ajouter le handle de la nouvelle
     page, sinon aucun Lead n'est envoyé à Meta. */
  function publishMetaLead(cfg, data, meta) {
    try {
      if (!window.Shopify || !Shopify.analytics || typeof Shopify.analytics.publish !== 'function') return;
      Shopify.analytics.publish('neurodidacte:lead_submitted', {
        event_id: meta.event_id,
        page_handle: cfg.pageHandle,
        form_name: cfg.formName,
        email: data.email || '',
        first_name: data.prenom || '',
        last_name: data.nom || '',
        value: 0,
        currency: 'CAD'
      });
    } catch (e) {
      /* l'analytique ne doit jamais bloquer une soumission */
    }
  }

  /* --- Conversion Google Ads « prospect » -------------------------------------
     Jumelle de publishMetaLead. L'étiquette vient de data-gads-label ; vide =
     rien n'est envoyé. Voir assets/nd-google-ads-lead.js. */
  function publishGoogleAdsLead(cfg, data, meta) {
    if (typeof window.ndTrackGoogleAdsLead !== 'function') return;
    window.ndTrackGoogleAdsLead({
      label: cfg.gadsLabel,
      event_id: meta.event_id,
      email: data.email || '',
      first_name: data.prenom || '',
      last_name: data.nom || ''
    });
  }

  function readCookie(name) {
    var m = document.cookie.match('(?:^|; )' + name + '=([^;]*)');
    return m ? decodeURIComponent(m[1]) : '';
  }

  function buildMeta() {
    var eventId =
      window.crypto && crypto.randomUUID
        ? crypto.randomUUID()
        : Date.now() + '-' + Math.random().toString(36).slice(2);

    var fbc = readCookie('_fbc');
    if (!fbc) {
      var fbclid = new URLSearchParams(location.search).get('fbclid');
      if (fbclid) fbc = 'fb.1.' + Date.now() + '.' + fbclid;
    }

    return {
      event_id: eventId,
      fbp: readCookie('_fbp'),
      fbc: fbc,
      event_source_url: window.location.href,
      client_user_agent: navigator.userAgent,
      event_time: Math.floor(Date.now() / 1000)
    };
  }

  /* ------------------------------- UI d'erreur ------------------------------- */

  function setFieldError(el, message) {
    if (!el) return;
    var field = el.closest('.nd-field') || el.parentNode;
    field.classList.add('nd-field--error');
    var existing = field.querySelector('.nd-field__error');
    if (existing) existing.remove();
    var span = document.createElement('span');
    span.className = 'nd-field__error';
    span.textContent = message;
    field.appendChild(span);
  }

  function clearFieldError(el) {
    if (!el) return;
    var field = el.closest('.nd-field') || el.parentNode;
    field.classList.remove('nd-field--error');
    var existing = field.querySelector('.nd-field__error');
    if (existing) existing.remove();
  }

  function showFormError(form, message) {
    var box = form.querySelector('.nd-form__error');
    if (!box) {
      box = document.createElement('div');
      box.className = 'nd-form__error';
      box.setAttribute('role', 'alert');
      box.setAttribute('tabindex', '-1');
      form.appendChild(box);
    }
    box.textContent = message;
    box.hidden = false;
    box.focus();
  }

  function clearFormError(form) {
    var box = form.querySelector('.nd-form__error');
    if (box) box.hidden = true;
  }

  /* --------------------------------- Init ----------------------------------- */

  function init(form) {
    if (form.dataset.ndBound === 'true') return;
    form.dataset.ndBound = 'true';

    var cfg = readConfig(form);
    var submitBtn = form.querySelector('[type="submit"]');
    var emailInput = form.querySelector('[name="contact[email]"]');
    var prenomInput = form.querySelector('[name="contact[prenom]"]');
    var consentInput = form.querySelector('[data-nd-consent]');
    var segmentInputs = form.querySelectorAll('[name="contact[segment]"]');
    var otherWrap = form.querySelector('[data-nd-other-wrap]');
    var otherInput = form.querySelector('[name="contact[segment_autre]"]');
    var segmentGroup = form.querySelector('[data-nd-segment-group]');

    // Champ « Autre » : révélé seulement quand l'option `autre` est cochée.
    if (otherWrap && segmentInputs.length) {
      Array.prototype.forEach.call(segmentInputs, function (radio) {
        radio.addEventListener('change', function () {
          var isOther = radio.checked && radio.value === 'autre';
          otherWrap.hidden = !isOther;
          if (isOther && otherInput) otherInput.focus();
          if (!isOther && otherInput) otherInput.value = '';
          if (segmentGroup) clearFieldError(segmentGroup);
        });
      });
    }

    if (emailInput) {
      emailInput.addEventListener('input', function () {
        if (isValidEmail(emailInput.value.trim())) clearFieldError(emailInput);
      });
    }
    if (consentInput) {
      consentInput.addEventListener('change', function () {
        if (consentInput.checked) clearFieldError(consentInput);
      });
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      e.stopImmediatePropagation();

      clearFormError(form);
      clearFieldError(emailInput);
      clearFieldError(prenomInput);
      if (segmentGroup) clearFieldError(segmentGroup);
      if (consentInput) clearFieldError(consentInput);

      // --- Validation (le formulaire est `novalidate` : tout se joue ici) ---
      if (prenomInput && !prenomInput.value.trim()) {
        setFieldError(prenomInput, 'Veuillez indiquer votre prénom.');
        prenomInput.focus();
        return;
      }
      var email = emailInput ? emailInput.value.trim() : '';
      if (!email) {
        setFieldError(emailInput, 'Veuillez indiquer votre courriel.');
        emailInput.focus();
        return;
      }
      if (!isValidEmail(email)) {
        setFieldError(emailInput, 'Cette adresse ne semble pas valide (exemple : prenom@exemple.com).');
        emailInput.focus();
        return;
      }
      if (segmentInputs.length && !form.querySelector('[name="contact[segment]"]:checked')) {
        setFieldError(segmentGroup, 'Veuillez choisir la situation qui vous décrit le mieux.');
        if (segmentGroup) segmentGroup.scrollIntoView({ block: 'center', behavior: 'smooth' });
        return;
      }
      if (consentInput && !consentInput.checked) {
        setFieldError(consentInput, cfg.consentError);
        consentInput.focus();
        return;
      }

      // --- Anti double-soumission (sinon : 2 inscriptions, campagne déclenchée 2 fois) ---
      if (form.dataset.ndBusy === 'true') return;
      form.dataset.ndBusy = 'true';

      var originalLabel = submitBtn ? submitBtn.textContent : '';
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = submitBtn.dataset.busyLabel || 'Envoi en cours…';
      }

      var data = harvest(form);
      var meta = buildMeta();

      publishMetaLead(cfg, data, meta);
      publishGoogleAdsLead(cfg, data, meta);

      // Customer.io est la SEULE destination qui prouve la capture.
      // Les autres sont « meilleur effort » et ne peuvent pas faire échouer l'envoi.
      var cioPromise = sendToCustomerIo(cfg, data);
      var others = Promise.all([
        sendToHubSpot(cfg, data),
        notifyShopify(cfg, data),
        sendToWebhook(cfg, data, meta)
      ]);

      cioPromise
        .then(function () {
          return others;
        })
        .then(function () {
          var dest = cfg.redirectUrl || '/';
          // Le courriel n'est JAMAIS propagé dans l'URL : il fuiterait vers tous
          // les trackers de la page de destination.
          var params = new URLSearchParams();
          if (data.prenom) params.set('prenom', data.prenom);
          params.set('source', cfg.formName);
          window.location.href = dest + (dest.indexOf('?') > -1 ? '&' : '?') + params.toString();
        })
        .catch(function () {
          // Customer.io injoignable (bloqueur, réseau, délai dépassé).
          // On NE redirige PAS : afficher un faux « C'est confirmé » perdrait le lead.
          form.dataset.ndBusy = 'false';
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.textContent = originalLabel;
          }
          showFormError(
            form,
            "Votre inscription n'a pas pu être enregistrée. Un bloqueur de publicité ou votre connexion en est peut-être la cause. Désactivez le bloqueur pour cette page, puis réessayez. Si le problème persiste, écrivez-nous à info@neurodidacte.com."
          );
        });
    });
  }

  function boot() {
    document.querySelectorAll('form[data-nd-capture]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Le Theme Editor recharge les sections sans recharger la page.
  document.addEventListener('shopify:section:load', boot);
})();
